import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { Alert, Avatar, Box, Button, CircularProgress, FormControlLabel, IconButton, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { alpha } from "@mui/material/styles";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { rose } from "@/theme/colors";
import { useAuth } from "@/context/AuthContext";
import { FOLLOW_CONFIRM_PREF_EVENT, getSkipFollowTagConfirm, setSkipFollowTagConfirm } from "@/utils/localStore";
import { useGetMyProfessionalQuery } from "@/src/services/api";
import { DISCIPLINE_TAGS, PRODUCT_TAGS, TAG_COLORS } from "@/data/presetTags";
import { useTagPreferences } from "@/hooks/useTagPreferences";
import { setSavedFilters } from "@/utils/localStore";
import { useDeleteProfileImageMutation, useUploadProfileImageMutation } from "@/src/services/uploadApi";
import { TimezoneSelect } from "@/components/Importable/Inputs";
import TagTogglePicker from "@/components/Importable/TagTogglePicker";
import { buildRegionTagItems } from "@/components/Importable/regionTagItems";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const emptyProfile = {
  firstName: "",
  lastName: "",
  city: "",
  state: "",
  country: "",
  timezone: ""
};
const emptyTags = {
  regions: [],
  disciplines: [],
  products: []
};
export default function ProfileForm({
  profile,
  profileImageUrl,
  isSaving,
  error,
  onSave,
  onImageUploaded,
  submitLabel = "Save changes",
  savingLabel = "Saving…",
  showCancel = false,
  onCancel,
  showPublishProfile = false
}) {
  const initialProfile = profile ?? emptyProfile;
  const [form, setForm] = useState(initialProfile);
  const [localError, setLocalError] = useState(null);
  const router = useRouter();
  const {
    isAuthenticated
  } = useAuth();
  // The signed-in user's published professional profile (404 → isError when
  // none exists). Skipped entirely unless the publish shortcut is requested.
  const {
    data: myProfessional,
    isLoading: myProfessionalLoading,
    isError: myProfessionalCheckFailed
  } = useGetMyProfessionalQuery(undefined, {
    skip: !showPublishProfile || !isAuthenticated
  });
  // 404 resolves to data:null (success). Hide the shortcut on real fetch
  // failures so it never appears for a user who already has a page.
  const showPublishButton = showPublishProfile && !myProfessionalLoading && !myProfessionalCheckFailed && myProfessional == null;
  // (Already-published users get an "Edit My Profile" shortcut in the
  // modal's title row instead — see ProfileDetailsModal.)
  const {
    prefs: tagPrefs,
    replaceAll
  } = useTagPreferences();
  const [uploadProfileImage, {
    isLoading: isUploading
  }] = useUploadProfileImageMutation();
  const [deleteProfileImage, {
    isLoading: isRemoving
  }] = useDeleteProfileImageMutation();
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Follow-tag confirmation preference (persisted outside the form —
  // toggling applies immediately, no save needed).
  const [skipFollowConfirm, setSkipFollowConfirmState] = useState(getSkipFollowTagConfirm);
  const [skipTipOpen, setSkipTipOpen] = useState(false);
  useEffect(() => {
    const sync = () => setSkipFollowConfirmState(getSkipFollowTagConfirm());
    window.addEventListener(FOLLOW_CONFIRM_PREF_EVENT, sync);
    return () => window.removeEventListener(FOLLOW_CONFIRM_PREF_EVENT, sync);
  }, []);
  const [localTags, setLocalTags] = useState(emptyTags);
  // Super-region groups + flat tag lists for the shared picker.
  const regionTagItems = useMemo(() => buildRegionTagItems(), []);
  // Tags seed once per mount — a profile refetch mid-edit (e.g. after an
  // avatar upload triggers refreshUser) must not wipe un-saved toggles.
  const tagsSeededRef = useRef(false);

  // Seed form + tags from props
  useEffect(() => {
    setForm(profile ?? emptyProfile);
    setLocalError(null);
    if (!tagsSeededRef.current) {
      tagsSeededRef.current = true;
      setLocalTags({
        ...tagPrefs
      });
    }
    setPendingFile(null);
    setPreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      setPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);
  const handleAvatarUpload = useCallback(event => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setPendingFile(file);
    setLocalError(null);
    event.target.value = "";
  }, []);
  const handleChange = field => event => {
    setForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };
  const clearPreviewSelection = useCallback(() => {
    setPreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPendingFile(null);
  }, []);
  const handleRemoveAvatar = useCallback(async () => {
    try {
      clearPreviewSelection();
      if (!profileImageUrl) {
        onImageUploaded?.(null);
        return;
      }
      await deleteProfileImage().unwrap();
      onImageUploaded?.(null);
      setLocalError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to remove profile photo.";
      setLocalError(message);
    }
  }, [clearPreviewSelection, deleteProfileImage, onImageUploaded, profileImageUrl]);
  const handleSubmit = async () => {
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      country: form.country.trim(),
      timezone: form.timezone.trim(),
      // Persisted server-side (metadata.followedTags) so the toggles
      // come back on by default even after local storage is cleared.
      followedTags: {
        regions: [...localTags.regions],
        disciplines: [...localTags.disciplines],
        products: [...localTags.products]
      }
    };
    if (!payload.firstName || !payload.lastName) {
      setLocalError("First and last name are required.");
      return;
    }
    try {
      if (pendingFile) {
        const res = await uploadProfileImage(pendingFile).unwrap();
        onImageUploaded?.(res.medium);
        setPendingFile(null);
      }
      await onSave(payload);
      replaceAll(localTags);
      try {
        setSavedFilters({
          regions: localTags.regions
        });
      } catch {}
      setLocalError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to save profile.";
      setLocalError(message);
    }
  };
  const helperText = localError ?? error ?? undefined;
  return /*#__PURE__*/_jsxs(Stack, {
    spacing: 2,
    children: [(localError || error) && /*#__PURE__*/_jsx(Alert, {
      severity: "error",
      sx: {
        mb: 1
      },
      children: helperText
    }), /*#__PURE__*/_jsxs(Stack, {
      direction: "row",
      spacing: {
        xs: 2,
        sm: 3
      },
      alignItems: "flex-start",
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          textAlign: "center",
          flexShrink: 0
        },
        children: [/*#__PURE__*/_jsxs(Box, {
          sx: {
            position: "relative",
            cursor: "pointer"
          },
          onClick: () => fileInputRef.current?.click(),
          children: [/*#__PURE__*/_jsx(Avatar, {
            src: previewUrl ?? profileImageUrl ?? undefined,
            alt: "Profile",
            sx: {
              width: {
                xs: 72,
                sm: 96
              },
              height: {
                xs: 72,
                sm: 96
              },
              fontSize: {
                xs: 26,
                sm: 34
              },
              bgcolor: alpha(rose.primary, 0.15),
              color: rose.dark,
              border: `3px solid ${alpha(rose.primary, 0.35)}`
            }
          }), /*#__PURE__*/_jsx(Box, {
            sx: {
              position: "absolute",
              bottom: 2,
              right: 2,
              bgcolor: "primary.main",
              borderRadius: "50%",
              width: 26,
              height: 26,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            },
            children: isUploading ? /*#__PURE__*/_jsx(CircularProgress, {
              size: 14,
              sx: {
                color: "#fff"
              }
            }) : /*#__PURE__*/_jsx(CameraAltIcon, {
              sx: {
                fontSize: 14,
                color: "#fff"
              }
            })
          }), /*#__PURE__*/_jsx("input", {
            ref: fileInputRef,
            type: "file",
            accept: "image/*",
            hidden: true,
            onChange: handleAvatarUpload
          })]
        }), (previewUrl || profileImageUrl) && /*#__PURE__*/_jsx(Button, {
          size: "small",
          color: "inherit",
          onClick: handleRemoveAvatar,
          disabled: isSaving || isUploading || isRemoving,
          sx: {
            mt: 0.5,
            fontSize: 11
          },
          children: isRemoving ? "Removing..." : "Remove photo"
        })]
      }), /*#__PURE__*/_jsxs(Stack, {
        direction: {
          xs: "column",
          sm: "row"
        },
        spacing: 2,
        sx: {
          flexGrow: 1,
          width: "100%",
          pt: {
            sm: 1.5
          }
        },
        children: [/*#__PURE__*/_jsx(TextField, {
          autoFocus: true,
          required: true,
          fullWidth: true,
          label: "First name",
          value: form.firstName,
          onChange: handleChange("firstName")
        }), /*#__PURE__*/_jsx(TextField, {
          required: true,
          fullWidth: true,
          label: "Last name",
          value: form.lastName,
          onChange: handleChange("lastName")
        })]
      })]
    }), /*#__PURE__*/_jsxs(Stack, {
      direction: {
        xs: "column",
        sm: "row"
      },
      spacing: 2,
      children: [/*#__PURE__*/_jsx(TextField, {
        fullWidth: true,
        label: "City",
        value: form.city,
        onChange: handleChange("city")
      }), /*#__PURE__*/_jsx(TextField, {
        fullWidth: true,
        label: "State / Province",
        value: form.state,
        onChange: handleChange("state")
      })]
    }), /*#__PURE__*/_jsxs(Stack, {
      direction: {
        xs: "column",
        sm: "row"
      },
      spacing: 2,
      children: [/*#__PURE__*/_jsx(TextField, {
        fullWidth: true,
        label: "Country",
        value: form.country,
        onChange: handleChange("country")
      }), /*#__PURE__*/_jsx(TimezoneSelect, {
        fullWidth: true,
        value: form.timezone,
        onChange: v => setForm(f => ({
          ...f,
          timezone: v
        }))
      })]
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "body2",
      color: "text.secondary",
      children: "These are tags you are following to have priority when being shown pages relevant to you."
    }), /*#__PURE__*/_jsx(TagTogglePicker, {
      label: "Regional Expertise",
      items: regionTagItems,
      value: localTags.regions,
      onChange: next => setLocalTags(prev => ({
        ...prev,
        regions: next
      })),
      color: TAG_COLORS.region.primary,
      colorDark: TAG_COLORS.region.dark,
      inModal: true
    }), /*#__PURE__*/_jsx(TagTogglePicker, {
      label: "Disciplines",
      items: [...DISCIPLINE_TAGS],
      value: localTags.disciplines,
      onChange: next => setLocalTags(prev => ({
        ...prev,
        disciplines: next
      })),
      color: TAG_COLORS.discipline.primary,
      colorDark: TAG_COLORS.discipline.dark,
      inModal: true
    }), /*#__PURE__*/_jsx(TagTogglePicker, {
      label: "Product Expertise",
      items: [...PRODUCT_TAGS],
      value: localTags.products,
      onChange: next => setLocalTags(prev => ({
        ...prev,
        products: next
      })),
      color: TAG_COLORS.product.primary,
      colorDark: TAG_COLORS.product.dark,
      inModal: true
    }), /*#__PURE__*/_jsxs(Stack, {
      direction: "row",
      alignItems: "center",
      sx: {
        mt: 1
      },
      children: [/*#__PURE__*/_jsx(FormControlLabel, {
        control: /*#__PURE__*/_jsx(Switch, {
          size: "small",
          checked: skipFollowConfirm,
          onChange: e => {
            setSkipFollowTagConfirm(e.target.checked);
            setSkipFollowConfirmState(e.target.checked);
          }
        }),
        label: /*#__PURE__*/_jsx(Typography, {
          variant: "body2",
          children: "Skip follow-tag confirmation"
        }),
        sx: {
          mr: 0.5
        }
      }), /*#__PURE__*/_jsx(Tooltip, {
        arrow: true,
        open: skipTipOpen,
        onOpen: () => setSkipTipOpen(true),
        onClose: () => setSkipTipOpen(false),
        title: "When on, tapping a tag chip on a page follows or unfollows it instantly. Turn this off to get the confirmation prompt again.",
        children: /*#__PURE__*/_jsx(IconButton, {
          size: "small",
          "aria-label": "About skip follow-tag confirmation",
          onClick: () => setSkipTipOpen(v => !v),
          children: /*#__PURE__*/_jsx(InfoOutlinedIcon, {
            fontSize: "inherit"
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(Stack, {
      direction: "row",
      spacing: 2,
      justifyContent: "space-between",
      alignItems: "center",
      sx: {
        pt: 1
      },
      children: [/*#__PURE__*/_jsx(Stack, {
        direction: "row",
        spacing: 1.5,
        alignItems: "center",
        children: showPublishButton && /*#__PURE__*/_jsx(Button, {
          variant: "outlined",
          disabled: isSaving,
          onClick: () => {
            void router.push("/professionals/create");
            onCancel?.();
          },
          sx: {
            color: rose.primary,
            borderColor: rose.primary,
            "&:hover": {
              color: rose.dark,
              borderColor: rose.dark,
              backgroundColor: alpha(rose.primary, 0.06)
            }
          },
          children: "Publish My Profile"
        })
      }), /*#__PURE__*/_jsxs(Stack, {
        direction: "row",
        spacing: 2,
        alignItems: "center",
        children: [showCancel && onCancel && /*#__PURE__*/_jsx(Button, {
          color: "inherit",
          onClick: onCancel,
          disabled: isSaving,
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          onClick: handleSubmit,
          disabled: isSaving || isUploading || isRemoving,
          variant: "contained",
          children: isSaving || isUploading || isRemoving ? savingLabel : submitLabel
        })]
      })]
    })]
  });
}